/**
 * Loading and empty states for the quote and sales-order screens — the L5
 * pattern, reused rather than re-invented.
 *
 * ===========================================================================
 * THE EMPTY CARD IS `InquiryEmptyCard`, IMPORTED
 * ===========================================================================
 * `features/inquiries/states.tsx` already owns the designed `Table/Empty`
 * (12:11359): a 480px max-width card with a dashed `border/default`, a `Spot`
 * where the Figma dot grid was, centred `Body/14` copy in `text/secondary`, and
 * an `accent/indigo` CTA. Building a second one here would put two dashed empty
 * cards in one app that drift apart the first time either is adjusted.
 *
 * Its NAME says "Inquiry" and it is used on a quote screen, which is worth
 * saying out loud rather than quietly living with: the component is generic (it
 * takes a message, a CTA and a spot; it knows nothing about inquiries) and
 * renaming it would touch a file this unit does not own for a cosmetic reason.
 * Recorded, not fixed.
 *
 * ===========================================================================
 * NO NEW SPOT WAS DRAWN
 * ===========================================================================
 * `SPOT_NAMES` is `inquiries | column | search | activity`, and the budget is
 * "one idea in three to five shapes" per mark. The `inquiries` spot draws a fresh
 * sheet on top of a stack that is not there yet — which is a DOCUMENT, and is
 * exactly what an empty quote list is missing. `search` carries the filtered
 * case. A fifth mark for "no quotes" would be a new asset drawn to say the same
 * thing the existing one already says.
 *
 * ===========================================================================
 * THE SKELETONS ARE THE DESIGN'S, AT THE HEIGHT OF THE REAL ROWS
 * ===========================================================================
 * `Table/SkeletonRow` (6:328) — bars filled `border/default` (NOT `bg/subtle`;
 * the design uses the border colour for skeleton bars, which is why the shared
 * `SkeletonRows` primitive is not reused for the table). The widths mirror the
 * quote table's own columns so the layout does not jump when the rows land.
 */

import { Link } from 'react-router-dom';
import { InquiryEmptyCard } from '../inquiries/states';
import { Alert } from '../../components/ui';
import { statusLabelInSentence } from '../../domain/quotes';
import { cn } from '../../lib/cn';

/* ──────────────────────────────────────────────────────────────── skeleton ── */

function SkeletonBar({ className }: { className?: string }): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse bg-line motion-reduce:animate-none', className)}
    />
  );
}

export function DocumentSkeletonTable({
  rows = 6,
  label,
}: {
  rows?: number;
  label: string;
}): React.JSX.Element {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
      className="overflow-hidden rounded-md border border-line bg-surface"
    >
      {Array.from({ length: rows }, (_unused, index) => (
        <div
          key={index}
          className="crf-register-row flex items-center gap-6 border-b border-line px-4 last:border-b-0"
        >
          <SkeletonBar className="h-2.5 w-[110px] rounded-pill" />
          <SkeletonBar className="h-2.5 w-[180px] rounded-pill" />
          <SkeletonBar className="h-5 w-[86px] rounded-pill" />
          <SkeletonBar className="ml-auto h-2.5 w-[96px] rounded-pill" />
          <SkeletonBar className="h-2.5 w-10 rounded-pill" />
        </div>
      ))}
    </div>
  );
}

/**
 * The WORKLIST's loading shape (WT3) — 52px two-line rows, matching the real
 * register's geometry so nothing jumps when the rows land.
 *
 * FIRST CONSUMER OF THE WT1 SKELETON PAIR (`bg-skeleton`, MC-2's 1.30–1.45
 * window; the pulse trough token pairs with it): the shared `SkeletonBar`
 * above keeps `bg-line` for the retired-shape skeletons that still use it —
 * migrating those (`SkeletonBlock` and friends) is WEB-TIER-A-LEDGER item 10,
 * filed, not folded here.
 */
function WorklistSkeletonBar({ className }: { className?: string }): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={cn(
        // `radius/pill` — the authored radius every skeleton bar in this file
        // already wears; the pair token carries the MC-2 contrast window.
        'animate-pulse rounded-pill bg-skeleton motion-reduce:animate-none',
        className,
      )}
    />
  );
}

export function WorklistSkeleton({
  rows = 6,
  label,
}: {
  rows?: number;
  label: string;
}): React.JSX.Element {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
      className="overflow-hidden rounded-md border border-line bg-surface"
    >
      {Array.from({ length: rows }, (_unused, index) => (
        <div
          key={index}
          className="crf-register-row flex items-center gap-3 border-b border-line px-4 last:border-b-0"
        >
          {/* No `py-*` — see the note on the real row in `QuotesListPage`. The
            * skeleton must be able to reach the same stops the row reaches, or
            * the list jumps the moment the data lands. */}
          <div className="flex flex-1 flex-col gap-1.5">
            <WorklistSkeletonBar className="h-3 w-[180px]" />
            <WorklistSkeletonBar className="h-2.5 w-[260px]" />
          </div>
          <WorklistSkeletonBar className="ml-auto h-3.5 w-[120px]" />
          <WorklistSkeletonBar className="h-2.5 w-14" />
        </div>
      ))}
    </div>
  );
}

/** The record page's own loading shape: header, then a table-sized block. */
export function DocumentSkeletonRecord(): React.JSX.Element {
  return (
    <div aria-busy="true" aria-label="Loading the document" className="flex flex-col gap-4">
      <SkeletonBar className="h-3 w-64 rounded-pill" />
      <SkeletonBar className="h-7 w-80 rounded-pill" />
      <SkeletonBar className="h-64 w-full rounded-md" />
    </div>
  );
}

/* ──────────────────────────────────────────────────────── out of range ── */

/**
 * `?page=3` on a result set that ends at page 2.
 *
 * ===========================================================================
 * AN EMPTY PAGE IS NOT AN EMPTY RESULT SET, AND THIS IS THE DIFFERENCE
 * ===========================================================================
 * Both list screens chose their empty card on `rows.length === 0` alone, so a
 * page past the end rendered a card that CONTRADICTED the screen around it in
 * one DOM: `/countries/IN/sales-orders?status=pushed&page=3` over a 60-row
 * result read
 *
 *   "Sales orders · 60 orders in IN · … · No orders are pushed to Tally —
 *    there are 60 in IN altogether."
 *
 * — the empty card denying, in the same breath, what the subtitle and its own
 * sentence had just counted. The All tab was worse: "60 quotes in IN" six nodes
 * above "No quotes yet".
 *
 * So this branch is NAVIGATION, not emptiness, and it says NOTHING about whether
 * records exist. It states the one fact the request proves — which page this list
 * ends at — and hands back a way to get there. `tone="info"`: nothing failed and
 * nothing is missing, exactly as `SalesOrderRecordPage`'s "Awaiting Tally sync"
 * and `QuoteRecordPage`'s "There is no pending request this quote could have"
 * use the same tone for a dead end that is not an error.
 *
 * The CTA is a real `<Link>`, not a button that calls a pager: it goes to a URL,
 * and `SalesOrdersListPage` has no pager at all — its only route to `page=3` is
 * the address bar, which is also the only route back out.
 */
export function OutOfRangeNotice({
  page,
  total,
  perPage,
  firstPageHref,
}: {
  /** The page that was asked for — past the end, or this would not render. */
  page: number;
  /** Rows in THIS result set (the active tab), which is what pages divide. */
  total: number;
  perPage: number;
  /** The same list at page 1, every other filter kept. Built by the caller. */
  firstPageHref: string;
}): React.JSX.Element {
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  return (
    <Alert tone="info" title={`Page ${String(page)} is past the end of this list`}>
      <p>
        This list ends at page {String(lastPage)}.{' '}
        <Link to={firstPageHref} className="underline underline-offset-2 hover:decoration-2">
          Show the first page
        </Link>
      </p>
    </Alert>
  );
}

/* ─────────────────────────────────────────────────────────────────── empty ── */

/*
 * WT3: the D1a authored trio, replacing this file's inline copy of the retired
 * 4.22 `bg-indigo` fill (`hover:bg-indigo/90` was an alpha fade whose contrast
 * moved opposite directions per theme — `Button.tsx` holds the measurements).
 * The same class of copy in OTHER files is WEB-TIER-A-LEDGER item 11; this one
 * was not on that list (it was missed at filing) and is fixed here because the
 * file is in WT3's own surface — noted on the ledger, not silently absorbed.
 */
const CTA_CLASSES =
  'inline-flex h-9 items-center justify-center rounded-sm bg-indigo-strong px-3.5 text-body font-medium text-fg-inverse hover:bg-indigo-strong-hover active:bg-indigo-strong-active';

/**
 * W5. "there are 1 in IN altogether" shipped on both empty cards, and a
 * one-document country is the COMMONEST state of a pilot — the first quote
 * anybody raises produces exactly it. A count interpolated into a fixed sentence
 * is the standard way this happens; the fix is one function so the next sentence
 * that needs it cannot get it wrong independently.
 */
function areOrIs(count: number): string {
  return count === 1 ? 'is' : 'are';
}

/*
 * V2. Every sentence below drops a status LABEL into running prose, and the
 * obvious `statusLabel.toLowerCase()` case-folds the product name in "Pushed to
 * Tally" and "Awaiting Tally sync". `statusLabelInSentence` (domain/quotes.ts)
 * folds the leading character only — see its header for why that covers both
 * maps by construction instead of naming "Tally" here.
 *
 * It is used on the QUOTE sentences too, though no quote label holds a proper
 * noun today: "Draft" → "draft" is the same output either way, so this changes
 * nothing on that screen — it just means a quote status added later cannot
 * reintroduce the defect through the sentence next door.
 */

/**
 * The quote list's empty states.
 *
 * Three branches, in the order an operator asks the questions: "did my tab do
 * this?", then "is there anything here at all?". Each offers a REAL action — the
 * L5 rule — and the country-empty branch offers it only to somebody who may
 * actually take it.
 */
export function QuotesEmptyState({
  countryCode,
  status,
  statusLabel,
  totalInCountry,
  canCreate,
}: {
  countryCode: string;
  /** The active tab, or `null` for "All". */
  status: string | null;
  statusLabel: string;
  /** Quotes in this country across every status. */
  totalInCountry: number;
  canCreate: boolean;
}): React.JSX.Element {
  if (status !== null && totalInCountry > 0) {
    return (
      <InquiryEmptyCard
        spot="inquiries"
        message={`No ${statusLabelInSentence(statusLabel)} quotes — there ${areOrIs(totalInCountry)} ${String(totalInCountry)} in ${countryCode} altogether.`}
        cta={
          <Link to={`/countries/${countryCode}/quotes`} className={CTA_CLASSES}>
            Show all quotes
          </Link>
        }
      />
    );
  }

  if (status !== null) {
    return (
      <InquiryEmptyCard
        spot="inquiries"
        message={`No ${statusLabelInSentence(statusLabel)} quotes.`}
      />
    );
  }

  return (
    <InquiryEmptyCard
      spot="inquiries"
      message="No quotes yet — the first one starts with a customer and a product."
      cta={
        canCreate ? (
          <Link to={`/countries/${countryCode}/quotes/new`} className={CTA_CLASSES}>
            Build the first quote
          </Link>
        ) : undefined
      }
    />
  );
}

/**
 * The sales-order list's empty state.
 *
 * A sales order cannot be CREATED — it exists only because a quote was confirmed
 * (`POST /quotes/:id/confirm`, and there is no SO write surface of any kind). So
 * the CTA points at the quotes list, which is the honest next step, and NOT at a
 * "New sales order" button that has no endpoint behind it.
 *
 * ===========================================================================
 * V1 — `totalInCountry` IS `null` UNTIL IT IS KNOWN, AND `null` IS NOT ZERO
 * ===========================================================================
 * The country count is a SECOND round trip (`useSalesOrderCountryTotal`), so
 * there is a window in which the filtered list has landed and the count has not.
 * Coercing that window to `0` put "No sales orders yet — one appears here when a
 * quote is confirmed." on screen in a country that held orders — the exact
 * sentence W2 was raised to remove, moved into the loading window rather than
 * deleted.
 *
 * So the middle branch below says only what the TAB proves and makes no claim
 * about the country at all. That is not a new pattern: `QuotesEmptyState` has
 * always had the same tab-only branch, and `QuotesListPage`'s tabs render a
 * count of `null` as ABSENT rather than as zero, for the reason its header
 * states outright — a zero is a claim.
 */
export function SalesOrdersEmptyState({
  countryCode,
  status,
  statusLabel,
  totalInCountry,
  canViewQuotes,
  canPlaceOrder = false,
}: {
  countryCode: string;
  status: string | null;
  statusLabel: string;
  /** Orders in this country across every status, or `null` while unknown. */
  totalInCountry: number | null;
  canViewQuotes: boolean;
  /**
   * `quotes.create` AND `quotes.confirm` — the pair the direct-order flow spends. The
   * empty card is where a country's FIRST order gets raised, so the action belongs
   * here as well as in the header; defaulted false so a caller that has not thought
   * about it offers nothing rather than a control it never gated.
   */
  canPlaceOrder?: boolean;
}): React.JSX.Element {
  if (status !== null && totalInCountry !== null && totalInCountry > 0) {
    return (
      <InquiryEmptyCard
        spot="inquiries"
        message={`No orders are ${statusLabelInSentence(statusLabel)} — there ${areOrIs(totalInCountry)} ${String(totalInCountry)} in ${countryCode} altogether.`}
        cta={
          <Link to={`/countries/${countryCode}/sales-orders`} className={CTA_CLASSES}>
            Show all orders
          </Link>
        }
      />
    );
  }

  /*
   * The count is still in flight and a tab is active. The tab is empty — that is
   * this screen's own request answering — and NOTHING is yet known about the rest
   * of the country, so nothing is said about it. One sentence, no CTA, exactly as
   * `QuotesEmptyState`'s equivalent branch.
   */
  if (status !== null && totalInCountry === null) {
    return (
      <InquiryEmptyCard
        spot="inquiries"
        message={`No orders are ${statusLabelInSentence(statusLabel)}.`}
      />
    );
  }

  /*
   * Either the All tab (where this screen's OWN unfiltered request returned
   * nothing, so the sentence is backed by the list itself) or a tab on a country
   * the probe has confirmed empty.
   */
  return (
    <InquiryEmptyCard
      spot="inquiries"
      message={
        canPlaceOrder
          ? 'No sales orders yet — one appears here when a quote is confirmed, or when you ' +
            'raise an order directly.'
          : 'No sales orders yet — one appears here when a quote is confirmed.'
      }
      cta={
        /*
         * THE DIRECT ROUTE WINS THE SLOT when it is available. Both CTAs would be two
         * ways to reach the same outcome offered with equal weight, and the card has one
         * slot by design; the quotes list stays one click away in the nav either way.
         */
        canPlaceOrder ? (
          <Link to={`/countries/${countryCode}/sales-orders/new`} className={CTA_CLASSES}>
            New sales order
          </Link>
        ) : canViewQuotes ? (
          <Link to={`/countries/${countryCode}/quotes`} className={CTA_CLASSES}>
            Open the quotes list
          </Link>
        ) : undefined
      }
    />
  );
}
